import Icon from "./Icon";
import clamp from "lodash/clamp";
import isEqual from "lodash/isEqual";
import isFinite from "lodash/isFinite";
import PropTypes from "prop-types";
import React from "react";
import ReactDOM from "react-dom";
import RegionSelect from "react-region-select";
import * as colors from "../helpers/constants/colors";
import styles from "../styles/components/HeatMap.css";
import { makeClassNameFunctionForModule } from "../utils/cssModules";
import ReactHeatmap from "./ReactHeatmap";
import ScrollLockDiv from "./ScrollLockDiv";

export const HeatmapPointRadius = {
    Large: 90,
    Normal: 70,
    Small: 50,
};

const CHUNK_HEIGHT = 10000;

const classNames = makeClassNameFunctionForModule(styles);

const pointProps = {
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
};

const dimensionProps = {
    height: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
};

function Throbber({
    centerColor,
    centerStroke,
    centerStrokeColor,
    haloColor,
    haloStroke,
    pulseColor,
}) {
    const size = 64;
    const halfSize = size / 2;

    // We want the center circle to have an apparent radius of 11, but sometimes we draw a stroke around it. Strokes
    // are centered on the radius so we need to add half the stroke width to make sure the filled portion has radius=11
    const centerRadius = 11 + centerStroke / 2;

    const haloRadius = 18;
    const haloExtent = haloRadius + haloStroke / 2;

    // The mask's job is to draw a hole in the "pulse" circle so that it doesn't draw over the halo & center circles.
    // That means we want an apparent radius equal to haloExtent. The mask has a stroke of 15 so we have to account for
    // that as well.
    const maskRadius = haloExtent + 15 / 2;

    const shared = { cx: halfSize, cy: halfSize, strokeMiterlimit: 10 };
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox={`0 0 ${size} ${size}`}>
            <defs>
                <mask id="pulse-mask" x="0" y="0" width={size} height={size}>
                    <circle
                        {...shared}
                        r={maskRadius}
                        fill="none"
                        stroke="white"
                        strokeWidth="15"
                    />
                </mask>
            </defs>
            <circle
                {...shared}
                r={centerRadius}
                fill={centerColor}
                stroke={centerStrokeColor}
                strokeWidth={centerStroke}
            />
            <circle
                {...shared}
                r={haloRadius}
                fill="none"
                stroke={haloColor}
                strokeWidth={haloStroke}
            />
            <circle
                {...shared}
                r={halfSize}
                fill={pulseColor}
                opacity="0.4"
                mask="url(#pulse-mask)"
            >
                <animate
                    attributeName="r"
                    repeatCount="2"
                    dur="1s"
                    values={`${haloExtent}; ${halfSize}; ${haloExtent}`}
                    keyTimes="0; 0.5; 1"
                    keySplines=".42 0 1 1; 0 0 .59 1;"
                    fill="freeze"
                />
            </circle>
        </svg>
    );
}

class HeatMap extends React.Component {
    static FIXED_BOX_SIZE_IN_PX = 100;

    static propTypes = {
        image: PropTypes.shape({
            imageUrl: PropTypes.string.isRequired,
            ...dimensionProps,
        }).isRequired,

        className: PropTypes.string,

        data: PropTypes.arrayOf(PropTypes.shape(pointProps)).isRequired,

        highlightedPoint: PropTypes.shape(pointProps),

        highlightSize: PropTypes.number,

        selectedRegion: PropTypes.shape({
            ...pointProps,
            ...dimensionProps,
        }),

        showOverlay: PropTypes.bool,

        gradient: PropTypes.objectOf(PropTypes.string),
        minOpacity: PropTypes.number,
        maxOpacity: PropTypes.number,

        onSelectRegionChange: PropTypes.func.isRequired,
        radius: PropTypes.number,
    };

    static defaultProps = {
        showOverlay: true,
        gradient: {
            0.25: "#0000FF",
            0.55: colors.heatMap.palette.lime,
            0.85: colors.heatMap.palette.yellow,
            1.0: colors.heatMap.palette.magenta,
        },

        highlightSize: 32,

        minOpacity: 0,
        maxOpacity: 0.5,
        radius: HeatmapPointRadius.Normal,
    };

    constructor(props) {
        super(props);

        this.imageRef = null;
        this.containerRef = null;
        this.highlightedPointRef = null;

        this.resizeTimer = null;

        this.state = {
            imageLoaded: false,
            scaleFactor: null,
            isResizing: false,
        };

        this.storeImageElementRef = this.storeImageElementRef.bind(this);
        this.storeContainerElementRef = this.storeContainerElementRef.bind(
            this
        );
        this.storeHighlightedPoint = this.storeHighlightedPoint.bind(this);
        this.handleImageLoad = this.handleImageLoad.bind(this);
        this.handleWindowResize = this.handleWindowResize.bind(this);
        this.computeScaleFactor = this.computeScaleFactor.bind(this);
        this.renderRegion = this.renderRegion.bind(this);
        this.onSelectRegionChange = this.onSelectRegionChange.bind(this);
    }

    componentDidMount() {
        if (window) {
            window.addEventListener("resize", this.handleWindowResize);
        }
        // There probably isn't a highlighted point yet if we just mounted, but let's call the function just in case.
        this.scrollHighlightedPointIntoView();
    }

    componentWillUnmount() {
        if (window) {
            window.removeEventListener("resize", this.handleWindowResize);
        }
    }

    componentWillReceiveProps(nextProps) {
        // If image has changed, we need to clear out the image state because otherwise the heatmap overlay will
        // be rendered with stale image state until the new image is loaded.
        const nextPropsImage = nextProps && nextProps.image;
        const currentPropsImage = this.props && this.props.image;
        if (!isEqual(nextPropsImage, currentPropsImage)) {
            this.state = {
                imageLoaded: false,
                scaleFactor: null,
                isResizing: false
            };
        }
    }

    componentDidUpdate() {
        // This method is called when the component and its children are finished rendering. At this point we will have
        // references to the image & container elements so we can force the browser to scroll if necessary.
        this.scrollHighlightedPointIntoView();
    }

    scrollHighlightedPointIntoView() {
        // The heatmap technically supports multiple highlighted points even though we only ever highlight one at a
        // time. We'll only try to scroll automatically if there's only 1 point selected.
        if (!this.props.highlightedPoint || !this.highlightedPointRef) {
            return;
        }

        const pointElement = ReactDOM.findDOMNode(this.highlightedPointRef);
        const containerElement = ReactDOM.findDOMNode(this.containerRef);

        const pointTop = pointElement.offsetTop;
        const pointBottom = pointTop + pointElement.offsetHeight;

        const viewportTop = containerElement.scrollTop;
        const viewportBottom = viewportTop + containerElement.clientHeight;

        // If the point is not in the viewport, we try to center it in the viewport
        if (pointTop < viewportTop || pointBottom > viewportBottom) {
            const elementCenter = pointTop + pointElement.offsetHeight / 2;
            const containerCenter = containerElement.clientHeight / 2;

            // Enable smooth scrolling while we auto-scroll and revert to the configured value when we're done. Smooth
            // scrolling looks good when we're driving the scrolling, but it's awkward when the user's doing it.
            const scrollBehavior = containerElement.style.scrollBehavior;
            containerElement.style.scrollBehavior = "smooth";
            containerElement.scrollTop = elementCenter - containerCenter;
            containerElement.style.scrollBehavior = scrollBehavior;
        }
    }

    handleWindowResize() {
        // The heatmap only sets its width & height properties when it's mounted. If we want to update those values,
        // we have to unmount the heatmap and remount it. That means we need to render this component without the
        // heatmap and then render again with it. That's what this code aims to do.
        this.setState({ isResizing: true });
        if (this.resizeTimer !== null) {
            clearTimeout(this.resizeTimer);
            this.resizeTimer = null;
        }
        this.resizeTimer = setTimeout(
            () => this.setState({ isResizing: false }),
            250
        );
        this.computeScaleFactor();
    }

    handleImageLoad() {
        this.setState({ imageLoaded: true });
        this.computeScaleFactor();
    }

    storeImageElementRef(imageElement) {
        this.imageRef = imageElement;
        this.computeScaleFactor();
    }

    storeContainerElementRef(containerElement) {
        this.containerRef = containerElement;
        this.computeScaleFactor();
    }

    storeHighlightedPoint(pointElement) {
        this.highlightedPointRef = pointElement;
        this.computeScaleFactor();
    }

    computeScaleFactor() {
        if (this.imageRef) {
            const scaleFactor =
                this.imageRef.clientWidth / this.props.image.width;
            this.setState({ scaleFactor });
        }
    }

    renderHeatmap() {
        // Caller disabled the heatmap overlay
        if (!this.props.showOverlay) {
            return null;
        }

        // We don't have enough information to size the heatmap canvas yet
        if (
            this.state.isResizing ||
            !this.state.imageLoaded ||
            this.state.scaleFactor === null
        ) {
            return null;
        }

        const scaledRadius = Math.round(
            this.props.radius * this.state.scaleFactor
        );

        const scaledPoints = this.props.data
            .map((unscaledPoint) => {
                return {
                    x: Math.round(unscaledPoint.x * this.state.scaleFactor),
                    y: Math.round(unscaledPoint.y * this.state.scaleFactor),
                    value: 1.3,
                };
            })
            .sort((a, b) => a.x - b.x || a.y - b.y);

        // Chunks up the points into 10k pixel heatmap segments for faster canvas rendering.
        const chunks = {};
        scaledPoints.forEach((point) => {
            const chunkIndex = Math.floor(point.y / CHUNK_HEIGHT);
            if (!chunks[chunkIndex]) {
                chunks[chunkIndex] = [];
            }

            // Calculate the offset y coordinate from the start of the chunk instead of the total y.
            const offset = chunkIndex * CHUNK_HEIGHT;
            const offsetPoint = {
                ...point,
                y: point.y - offset,
            };

            chunks[chunkIndex].push(offsetPoint);
        });

        return (
            <div style={{ height: "100%", width: "100%" }}>
                {Object.values(chunks).map((chunk, index) => {
                    const chunkStartY = index * CHUNK_HEIGHT;
                    const chunkEndY = (index + 1) * CHUNK_HEIGHT;

                    // For initial chunks, use the max chunk height. For last chunk, set height to the remaining
                    // height.
                    let height;
                    if (chunkEndY < this.imageRef.clientHeight) {
                        height = CHUNK_HEIGHT;
                    } else {
                        height = this.imageRef.clientHeight - chunkStartY;
                    }

                    return (
                        <div key={`${this.props.image.imageUrl}-${index}`} style={{ height: `${height}px` }}>
                            <ReactHeatmap
                                data={chunk}
                                configObject={{
                                    radius: scaledRadius,
                                    gradient: this.props.gradient,
                                    minOpacity: this.props.minOpacity,
                                    maxOpacity: this.props.maxOpacity,
                                }}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }

    renderHighlightedPoint() {
        if (!this.props.highlightedPoint) {
            return null;
        }

        // We don't have enough information to scale the highlighted point(s) yet
        if (
            this.state.isResizing ||
            !this.state.imageLoaded ||
            this.state.scaleFactor === null
        ) {
            return null;
        }

        const highlightSize = this.props.highlightSize;

        const colors = this.props.showOverlay
            ? {
                  centerColor: "#1da4ff",
                  centerStroke: 0,
                  centerStrokeColor: "none",
                  haloColor: "#ffffff",
                  haloStroke: 3,
                  pulseColor: "#ffffff",
              }
            : {
                  centerColor: "#1da4ff",
                  centerStroke: 3,
                  centerStrokeColor: "#ffffff",
                  haloColor: "#1da4ff",
                  haloStroke: 3,
                  pulseColor: "#1da4ff",
              };

        const left = Math.round(
            this.props.highlightedPoint.x * this.state.scaleFactor -
                highlightSize / 2
        );
        const top = Math.round(
            this.props.highlightedPoint.y * this.state.scaleFactor -
                highlightSize / 2
        );
        return (
            <div
                key={`throbber(${left}, ${top})`}
                className={styles.highlightedPoint}
                style={{
                    position: "absolute",
                    height: highlightSize,
                    width: highlightSize,
                    left: left,
                    top: top,
                }}
                ref={this.storeHighlightedPoint}
            >
                <Throbber {...colors} />
            </div>
        );
    }

    shouldDisplayRegion() {
        return this.imageRef && this.props.selectedRegion;
    }

    renderRegion({ isChanging }) {
        if (isChanging || !this.shouldDisplayRegion()) {
            return null;
        }

        const region = this.props.selectedRegion;
        const { clientHeight } = this.imageRef;
        const bottomY = ((region.y + region.height) * clientHeight) / 100;
        const onBottomOfPage = clientHeight - bottomY < 35;
        return (
            <div>
                <div
                    onClick={() => this.props.onSelectRegionChange(null)}
                    {...classNames(
                        "heatmapSelectionRemove",
                        onBottomOfPage ? "regionAtBottom" : "default"
                    )}
                >
                    <p>
                        <Icon
                            icon="times"
                            className={styles.removeXIcon}
                        />{" "}
                        remove
                    </p>
                </div>
            </div>
        );
    }

    renderRegionOverlay() {
        if (!this.shouldDisplayRegion()) {
            return null;
        }

        const region = this.props.selectedRegion;
        const { clientWidth, clientHeight } = this.imageRef;

        // region coords are in percent so we convert to pixels
        const regionX = (region.x * clientWidth) / 100;
        const regionY = (region.y * clientHeight) / 100;
        const regionWidth = (region.width * clientWidth) / 100;
        const regionHeight = (region.height * clientHeight) / 100;

        // create a 'poke hole' effect for a selected region, by rendering
        // a div with borders that appear as the inverse of the selected region
        const top = regionY;
        const left = regionX;
        const right = clientWidth - regionX - regionWidth;
        const bottom = clientHeight - top - regionHeight;

        const widthString = [top, right, bottom, left]
            .map((coord) => `${coord}px`)
            .join(" ");
        return (
            <div
                className={styles.heatmapSelectionOverlay}
                style={{ borderWidth: widthString }}
            />
        );
    }

    onSelectRegionChange(regions) {
        const region = regions[0];

        // When we pass [] to the <RegionSelect> component, it notifies us about the change but the x, y, width,
        // and height fields are not present. We can safely ignore these calls.
        const newRegionIsValid =
            region &&
            ["x", "y", "width", "height"].every((p) => isFinite(region[p]));
        if (!newRegionIsValid) {
            return;
        }

        const newRegionIsSmall = region.height <= 1 && region.width <= 1;
        if (newRegionIsSmall) {
            if (this.props.selectedRegion) {
                // The user clicked but they already have a region selected. We interpret this as a request to clear
                // that region.
                this.props.onSelectRegionChange(null);
            } else {
                // The user clicked and there's no region selected. We'll draw a FIXED_BOX_SIZE_IN_PX square region
                // centered on the location they clicked.
                const widthPct =
                    (100 * HeatMap.FIXED_BOX_SIZE_IN_PX) /
                    this.imageRef.clientWidth;
                const heightPct =
                    (100 * HeatMap.FIXED_BOX_SIZE_IN_PX) /
                    this.imageRef.clientHeight;

                // make sure the x & y stay within the bounds of the heatmap
                const x = clamp(region.x - widthPct / 2, 0, 100 - widthPct);
                const y = clamp(region.y - heightPct / 2, 0, 100 - heightPct);

                const fixedSizeRegion = {
                    x,
                    y,
                    width: widthPct,
                    height: heightPct,
                };
                this.props.onSelectRegionChange(fixedSizeRegion);
            }
        } else {
            this.props.onSelectRegionChange(region);
        }
    }

    render() {
        // do not display region selection if it is a single point
        // this also ensures that clicking outside of region makes it disappear
        const shouldDisplayRegion = this.shouldDisplayRegion();

        return (
            <ScrollLockDiv
                className={this.props.className}
                ref={this.storeContainerElementRef}
            >
                <div className={styles.wrapper}>
                    {
                        // <img> is the only statically-positioned element so it defines the size of the <div>
                        // container. We don't know how big the container is going to be until the image finishes
                        // loading, so we trigger an update when that happens.
                    }
                    <img
                        className={`${styles.image} ${
                            this.props.showOverlay &&
                            !shouldDisplayRegion &&
                            styles.withOverlay
                        }`}
                        src={this.props.image.imageUrl}
                        onLoad={this.handleImageLoad}
                        ref={this.storeImageElementRef}
                    />
                    <div className={styles.heatmap}>
                        <RegionSelect
                            maxRegions={1}
                            {...classNames(
                                "heatmapSelection",
                                shouldDisplayRegion
                                    ? "showRegion"
                                    : "hideRegion"
                            )}
                            regions={
                                this.props.selectedRegion
                                    ? [this.props.selectedRegion]
                                    : []
                            }
                            onChange={this.onSelectRegionChange}
                            regionRenderer={this.renderRegion}
                            constraint={true}
                        >
                            {this.renderHeatmap()}
                            {this.renderHighlightedPoint()}
                            {this.renderRegionOverlay()}
                        </RegionSelect>
                    </div>
                </div>
            </ScrollLockDiv>
        );
    }
}

export default HeatMap;



// WEBPACK FOOTER //
// ./src/components/HeatMap.js